import React from 'react';
import Icon from '../../../components/AppIcon';

const ProcessOverview = () => {
  const processSteps = [
    {
      number: '01',
      title: 'Discovery & Strategy',
      description: 'We begin with comprehensive discovery sessions to understand your business objectives, technical requirements, and success metrics. Our team conducts thorough market research and competitive analysis to inform strategic decisions.',
      icon: 'Search',
      duration: '1-2 weeks',
      deliverables: ['Requirements Document', 'Technical Architecture', 'Project Roadmap']
    },
    {
      number: '02',
      title: 'Design & Prototyping',
      description: 'Our design team creates intuitive user experiences backed by data-driven insights. We develop interactive prototypes and conduct user testing to validate design decisions before development begins.',
      icon: 'Palette',
      duration: '2-3 weeks',
      deliverables: ['UI/UX Designs', 'Interactive Prototype', 'Design System']
    },
    {
      number: '03',
      title: 'Agile Development',
      description: 'Using agile methodologies, we build your solution in iterative sprints with continuous feedback loops. Our development process emphasizes code quality, security, and scalability from day one.',
      icon: 'Code2',
      duration: '8-16 weeks',
      deliverables: ['Working Software', 'API Documentation', 'Test Coverage']
    },
    {
      number: '04',
      title: 'Quality Assurance',
      description: 'Rigorous testing across multiple dimensions ensures your application meets the highest quality standards. We conduct functional, performance, security, and user acceptance testing.',
      icon: 'CheckCircle2',
      duration: '2-3 weeks',
      deliverables: ['Test Reports', 'Bug Fixes', 'Performance Metrics']
    },
    {
      number: '05',
      title: 'Deployment & Launch',
      description: 'We handle the complete deployment process with zero-downtime strategies and comprehensive monitoring. Our team ensures smooth transition to production with rollback capabilities.',
      icon: 'Rocket',
      duration: '1 week',
      deliverables: ['Production Deployment', 'Monitoring Setup', 'Launch Support']
    },
    {
      number: '06',
      title: 'Support & Optimization',
      description: 'Post-launch, we provide ongoing support, performance monitoring, and continuous optimization. Our team remains available for enhancements, scaling, and technical guidance.',
      icon: 'LifeBuoy',
      duration: 'Ongoing',
      deliverables: ['24/7 Support', 'Performance Reports', 'Feature Updates']
    }
  ];

  return (
    <section className="py-16 md:py-24 lg:py-32 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12 md:mb-16">
          <div className="inline-flex items-center space-x-2 bg-primary/10 px-4 py-2 rounded-full mb-4 md:mb-6">
            <Icon name="GitBranch" size={18} color="var(--color-primary)" />
            <span className="text-xs md:text-sm font-medium font-cta text-primary">
              Our Methodology
            </span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-headline text-foreground mb-4 md:mb-6">
            Transparent Development Process
          </h2>
          <p className="text-base md:text-lg text-muted-foreground font-body max-w-3xl mx-auto">
            Our proven six-phase methodology ensures predictable outcomes, transparent communication, and exceptional quality at every stage of your project journey.
          </p>
        </div>

        <div className="space-y-6 md:space-y-8 lg:space-y-12">
          {processSteps?.map((step, index) => (
            <div
              key={index}
              className="relative bg-card border border-border rounded-lg p-6 md:p-8 lg:p-10 hover:shadow-xl transition-all duration-300 hover:border-primary/50"
            >
              <div className="flex flex-col lg:flex-row lg:items-start lg:space-x-8">
                <div className="flex items-start space-x-4 lg:space-x-6 mb-6 lg:mb-0">
                  <div className="flex-shrink-0">
                    <div className="w-16 h-16 md:w-20 md:h-20 bg-primary/10 rounded-lg flex items-center justify-center">
                      <Icon name={step?.icon} size={32} color="var(--color-primary)" />
                    </div>
                  </div>
                  <div className="lg:hidden">
                    <span className="text-4xl md:text-5xl font-bold font-headline text-primary/20">
                      {step?.number}
                    </span>
                  </div>
                </div>

                <div className="hidden lg:block flex-shrink-0">
                  <span className="text-6xl font-bold font-headline text-primary/20">
                    {step?.number}
                  </span>
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-4">
                    <h3 className="text-xl md:text-2xl lg:text-3xl font-bold font-headline text-foreground mb-2 md:mb-0">
                      {step?.title}
                    </h3>
                    <span className="inline-flex items-center space-x-2 bg-muted px-3 py-1 rounded-full text-xs md:text-sm font-medium font-body text-muted-foreground whitespace-nowrap">
                      <Icon name="Clock" size={14} />
                      <span>{step?.duration}</span>
                    </span>
                  </div>

                  <p className="text-sm md:text-base text-muted-foreground font-body mb-6 leading-relaxed">
                    {step?.description}
                  </p>

                  <div className="bg-muted/50 rounded-lg p-4 md:p-6">
                    <h4 className="text-sm md:text-base font-semibold font-headline text-foreground mb-3 md:mb-4 flex items-center space-x-2">
                      <Icon name="Package" size={18} />
                      <span>Key Deliverables</span>
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                      {step?.deliverables?.map((deliverable, idx) => (
                        <div key={idx} className="flex items-center space-x-2">
                          <Icon name="CheckCircle2" size={16} color="var(--color-success)" className="flex-shrink-0" />
                          <span className="text-xs md:text-sm text-foreground font-body">
                            {deliverable}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {index < processSteps?.length - 1 && (
                <div className="hidden lg:block absolute left-1/2 -bottom-6 transform -translate-x-1/2">
                  <Icon name="ArrowDown" size={24} color="var(--color-primary)" />
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-12 md:mt-16 text-center">
          <a
            href="/process"
            className="inline-flex items-center space-x-2 bg-primary text-primary-foreground px-6 md:px-8 py-3 md:py-4 rounded-md font-medium font-cta text-sm md:text-base hover:bg-primary/90 transition-all duration-200 shadow-lg hover:shadow-xl"
          >
            <span>Explore Our Full Process</span>
            <Icon name="ArrowRight" size={18} />
          </a>
        </div>
      </div>
    </section>
  );
};

export default ProcessOverview;